import {
  useCreateBillingOrderPaymentMethodsSetDefaultByPaymentMethodId,
  useCreateBillingPaymentMethodsSetDefaultByPaymentMethodId,
  useCreateBillingSubscriptionPaymentMethodsSetDefaultByPaymentMethodId,
} from "@birdwatch/client";
import {
  Modal,
  ModalClose,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from "@birdwatch/ui";
import { cn } from "cn";
import { useState } from "react";

import {
  BwBankCardDoubleLine,
  BwCardsDollarLine,
  BwLoadingLine,
  BwPenRulerLine,
} from "@/components/ui/icons";
import { Typography } from "@/components/ui/typography";
import { useRefresh } from "@/features/shared/realtime-refresh";
import {
  type PaymentDefaultActionScope,
  PaymentDefaultScope,
} from "../_types/payment-default-scope";

type SetAsDefaultDialogProps = {
  /** Whether the dialog is open. */
  open: boolean;

  /** Callback to update the open state. */
  onOpenChange: (open: boolean) => void;

  /** The payment method to set as default. */
  paymentMethodId: string;

  /** Account ID to associate with the request. */
  accountId?: string | null;

  /** Called with the chosen scope after a successful mutation. */
  onSuccess?: (scope: PaymentDefaultActionScope) => void;

  /** Called when a mutation fails. */
  onError?: () => void;
};

type ScopeOptionProps = {
  paymentMethodId: string;
  accountId?: string | null;
  disabled: boolean;
  onStart: () => void;
  onSuccess: () => void;
  onError: () => void;
};

const BUTTON_CLASS =
  "flex cursor-pointer items-center gap-3 rounded-2xl border border-border-dark-low p-3 text-left transition-[background-color,transform] duration-150 ease-out hover:bg-bg-dark-min active:scale-[0.99] active:bg-bg-dark-min disabled:cursor-default disabled:opacity-50";

const OrderScopeOption = ({
  paymentMethodId,
  accountId,
  disabled,
  onStart,
  onSuccess,
  onError,
}: ScopeOptionProps) => {
  const refresh = useRefresh();
  const { mutateAsync, isPending } =
    useCreateBillingOrderPaymentMethodsSetDefaultByPaymentMethodId({
      // Returned, so the dialog closes over the payment methods' new default.
      // Without an `accountId` the route writes the caller's own account, which
      // this dialog cannot name.
      onSuccess: () =>
        accountId ? refresh("billing.changed", { accountId }) : undefined,
    });

  const handleClick = async () => {
    onStart();
    try {
      await mutateAsync({ params: { paymentMethodId }, body: { accountId } });
      onSuccess();
    } catch {
      onError();
    }
  };

  return (
    <button
      type="button"
      disabled={disabled || isPending}
      className={cn(BUTTON_CLASS, {
        "cursor-not-allowed": disabled || isPending,
      })}
      onClick={handleClick}
    >
      {isPending ? (
        <BwLoadingLine className="size-6 shrink-0 animate-spin text-text-dark-low" />
      ) : (
        <BwPenRulerLine className="size-6 shrink-0 text-text-success" />
      )}
      <Typography
        size="sm"
        className="min-w-0 flex-1 truncate text-text-dark-max"
      >
        Order payments
      </Typography>
    </button>
  );
};

const MembershipScopeOption = ({
  paymentMethodId,
  accountId,
  disabled,
  onStart,
  onSuccess,
  onError,
}: ScopeOptionProps) => {
  const refresh = useRefresh();
  const { mutateAsync, isPending } =
    useCreateBillingSubscriptionPaymentMethodsSetDefaultByPaymentMethodId({
      // Returned, so the dialog closes over the payment methods' new default.
      // Without an `accountId` the route writes the caller's own account, which
      // this dialog cannot name.
      onSuccess: () =>
        accountId ? refresh("billing.changed", { accountId }) : undefined,
    });

  const handleClick = async () => {
    onStart();
    try {
      await mutateAsync({ params: { paymentMethodId }, body: { accountId } });
      onSuccess();
    } catch {
      onError();
    }
  };

  return (
    <button
      type="button"
      disabled={disabled || isPending}
      className={cn(BUTTON_CLASS, {
        "cursor-not-allowed": disabled || isPending,
      })}
      onClick={handleClick}
    >
      {isPending ? (
        <BwLoadingLine className="size-6 shrink-0 animate-spin text-text-dark-low" />
      ) : (
        <BwCardsDollarLine className="size-6 shrink-0 text-text-blue" />
      )}
      <Typography
        size="sm"
        className="min-w-0 flex-1 truncate text-text-dark-max"
      >
        Membership payments
      </Typography>
    </button>
  );
};

const AccountScopeOption = ({
  paymentMethodId,
  accountId,
  disabled,
  onStart,
  onSuccess,
  onError,
}: ScopeOptionProps) => {
  const refresh = useRefresh();
  const { mutateAsync, isPending } =
    useCreateBillingPaymentMethodsSetDefaultByPaymentMethodId({
      // Returned, so the dialog closes over the payment methods' new default.
      // Without an `accountId` the route writes the caller's own account, which
      // this dialog cannot name.
      onSuccess: () =>
        accountId ? refresh("billing.changed", { accountId }) : undefined,
    });

  const handleClick = async () => {
    onStart();
    try {
      await mutateAsync({ params: { paymentMethodId }, body: { accountId } });
      onSuccess();
    } catch {
      onError();
    }
  };

  return (
    <button
      type="button"
      disabled={disabled || isPending}
      className={cn(BUTTON_CLASS, {
        "cursor-not-allowed": disabled || isPending,
      })}
      onClick={handleClick}
    >
      {isPending ? (
        <BwLoadingLine className="size-6 shrink-0 animate-spin text-text-dark-low" />
      ) : (
        <BwBankCardDoubleLine className="size-6 shrink-0 text-text-dark-max" />
      )}
      <Typography
        size="sm"
        className="min-w-0 flex-1 truncate text-text-dark-max"
      >
        Both
      </Typography>
    </button>
  );
};

/**
 * Dialog for selecting which payment scope a method should be set as default for.
 * Presents three options: Order payments, Membership payments, or Both.
 * Each option owns its mutation hook and shows a loading spinner while pending.
 * The dialog closes once a mutation settles.
 *
 * @param open - Whether the dialog is open.
 * @param onOpenChange - Callback to update the open state.
 * @param paymentMethodId - The payment method to set as default.
 * @param accountId - Account ID to associate with the request.
 * @param onSuccess - Called with the chosen scope after a successful mutation.
 * @param onError - Called when a mutation fails.
 *
 * @example
 * <SetAsDefaultDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   paymentMethodId={paymentMethodId}
 *   accountId={accountId}
 *   onSuccess={(scope) => pmToast.defaultUpdated(brandName, last4, scope)}
 *   onError={() => pmToast.defaultFailed()}
 * />
 */
export const SetAsDefaultDialog = ({
  open,
  onOpenChange,
  paymentMethodId,
  accountId,
  onSuccess,
  onError,
}: SetAsDefaultDialogProps) => {
  const [pendingScope, setPendingScope] =
    useState<PaymentDefaultActionScope | null>(null);

  const isAnyPending = pendingScope !== null;

  const makeHandlers = (scope: PaymentDefaultActionScope) => ({
    onStart: () => setPendingScope(scope),
    onSuccess: () => {
      setPendingScope(null);
      onOpenChange(false);
      onSuccess?.(scope);
    },
    onError: () => {
      setPendingScope(null);
      onError?.();
    },
  });

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent className="sm:max-w-sm">
        <ModalHeader>
          <ModalTitle />
          <ModalClose />
        </ModalHeader>

        <div className="flex flex-col gap-6 px-4 pb-4 pt-1">
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-semibold leading-7 text-text-dark-max">
              Apply payment default
            </h2>
            <Typography size="base" className="text-text-dark-high">
              Choose where this payment method should be used by default.
            </Typography>
          </div>

          <div className="flex flex-col gap-1">
            <Typography size="sm" className="font-medium text-text-dark-low">
              Set as default for
            </Typography>

            <div className="flex flex-col gap-1">
              <OrderScopeOption
                paymentMethodId={paymentMethodId}
                accountId={accountId}
                disabled={isAnyPending}
                {...makeHandlers(PaymentDefaultScope.Order)}
              />
              <MembershipScopeOption
                paymentMethodId={paymentMethodId}
                accountId={accountId}
                disabled={isAnyPending}
                {...makeHandlers(PaymentDefaultScope.Membership)}
              />
              <AccountScopeOption
                paymentMethodId={paymentMethodId}
                accountId={accountId}
                disabled={isAnyPending}
                {...makeHandlers(PaymentDefaultScope.Account)}
              />
            </div>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};
